import React, { useCallback, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { IconButton, Menu, MenuItem, Box } from '@mui/material'
import { Dotsmenu } from '@e3dc-react/icons'
import useModals from 'src/Hooks/useModals'
import { makeStyles } from 'tss-react/mui'
import { ApiPermissionType, ExtensionsType } from 'src/Libs/api/types'
import { useOwnerAccessRequest } from 'src/Libs/installation-rights-management/hooks'

interface Props {
  item: AccountLinkModel
  onRemove: (value: AccountLinkModel) => void
}

const useStyles = makeStyles<{ open: boolean }>()((theme, { open }) => ({
  dotsMenuIcon: {
    color: open ? theme.palette.primary.main : theme.palette.grey.grey00,
  },
}))

const ActionMenu: React.VFC<Props> = ({ item, onRemove }) => {
  const { t } = useCustomTranslation()
  const { openConfirmationModal, openInstallationAccessRightDialog } = useModals()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { classes } = useStyles({ open })

  const { accessRequests } = useOwnerAccessRequest(item.installationId)
  const isAccessBlocked = item?.permissionType === ApiPermissionType.blocked
  const isAccessPending = item?.permissionType === ApiPermissionType.pending
  const isAccessPendingOrBlocked = isAccessPending || isAccessBlocked

  const handleAccessRight = (): void => {
    if (!accessRequests || !item) return

    const accessRequest = accessRequests.find(request => request.accountLinkId === item?.id)

    if (!accessRequest) return

    closeMenu()
    openInstallationAccessRightDialog({ installationId: item.installationId, accessRequest })
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = (): void => {
    setAnchorEl(null)
  }

  const processRemove = useCallback(
    (close: () => void): void => {
      onRemove(item)
      close()
    },
    [onRemove, item]
  )

  const handleClose = useCallback((): void => {
    closeMenu()
  }, [])

  const handleRemoveDialog = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      const isLuoxEnergy: boolean = item.permissionType === ExtensionsType.luoxEnergy

      openConfirmationModal({
        onConfirm: processRemove,
        buttonText: [t('common.cancel'), t('common.invite-confirm')],
        title: t('installation.access-and-invitations.remove-link.confirm.title'),
        text: isLuoxEnergy
          ? t('installation.access-and-invitations.remove-link.confirm.lumenaza-description', { item: `'${item.userName}'` })
          : t('installation.access-and-invitations.remove-link.confirm.description', {
              item: item.companyName || `${item.firstName} ${item.lastName}`,
            }),
      })
      closeMenu()
    },
    [item.permissionType, item.userName, item.companyName, item.firstName, item.lastName, openConfirmationModal, processRemove, t]
  )

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <Dotsmenu className={classes.dotsMenuIcon} />
      </IconButton>
      <Menu id="action-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        {!isAccessPending && <MenuItem onClick={handleRemoveDialog}>{t('common.remove')}</MenuItem>}
        {isAccessPendingOrBlocked && <MenuItem onClick={handleAccessRight}>{t('common.edit')}</MenuItem>}
      </Menu>
    </Box>
  )
}

export default ActionMenu
